import { memo } from 'react';

import { CodeSegment, emphasizeMatches } from '@atlassian/bitkit-code';

import {
  ChunkEntry,
  DiffInlineRenderProp,
  LineAnnotation,
  OnAddComment,
  Line,
} from '../../common/types';
import { getCurrentLineAnnotations } from '../../common/util/get-line-annotations';
import { lineHasInlineContent } from '../../common/util/line-has-inline-content';
import { Region } from '../../common/word-diff/tokenizers';

import ChunkControls from './chunk-controls';
import { CodeLine } from './code-line';

type DiffLinesProps = {
  activePermalink?: string;
  chunk: ChunkEntry;
  chunkIndex?: number;
  filePath?: string;
  inlineContent?: DiffInlineRenderProp;
  onAddComment?: OnAddComment;
  onShowMoreLines: (isShiftClicked: boolean, shouldExpandDown: boolean) => void;
  showPermalinks: boolean;
  onPermalinkClick?: (permalink: string) => void;
  fileAnnotations?: LineAnnotation[];
  isPrAnnotationsEnabled?: boolean;
  isFirstChunk: boolean;
  isLastChunk: boolean;
  before: ChunkEntry;
  after: ChunkEntry;
  beforeHighlighted: CodeSegment[];
  afterHighlighted: CodeSegment[];
  isMultiChunkExpansionInDiffEnabled?: boolean;
  shouldHighlightDiffLine?: (diffLine: Line) => boolean;
  shouldHighlightLineDivider?: (
    chunkIndex: number,
    lineIndex: number
  ) => boolean;
  onSelectionStart?: (chunkIndex: number, lineIndex: number) => void;
  onSelectionMove?: (
    chunkIndex: number,
    lineIndex: number,
    currentChunk: ChunkEntry | { before: ChunkEntry; after: ChunkEntry }
  ) => void;
  onSelectionEnd?: () => void;
  isMultilineSelecting?: boolean;
  selectionChunkIndex?: number;
  selectionStartLineIndex?: number;
};

export const SideBySideDiffLines = memo(
  ({
    chunk,
    chunkIndex,
    onShowMoreLines,
    isFirstChunk,
    isLastChunk,
    before,
    after,
    activePermalink,
    filePath,
    showPermalinks,
    onPermalinkClick,
    onAddComment,
    fileAnnotations,
    inlineContent,
    isPrAnnotationsEnabled,
    beforeHighlighted,
    afterHighlighted,
    isMultiChunkExpansionInDiffEnabled,
    shouldHighlightDiffLine,
    shouldHighlightLineDivider,
    onSelectionStart,
    onSelectionMove,
    onSelectionEnd,
    isMultilineSelecting,
    selectionChunkIndex,
    selectionStartLineIndex,
  }: DiffLinesProps) => {
    const diffLines = before.changes.map((_, index) => {
      const beforeLine = before.changes[index];
      const afterLine = after.changes[index];

      // unchanged lines' content should only appear on left (before) side
      const isUnchanged =
        beforeLine.type === 'normal' && afterLine.type === beforeLine.type;

      // When multiline selecting on a side-by-side diff, we want to highlight
      // both dividers on before and after lines, so we can use the same index.
      const shouldHighlightDivider =
        shouldHighlightLineDivider?.(chunkIndex || 0, index) ?? false;

      return (
        <div
          className="lines-wrapper"
          key={`line-${beforeLine.oldLine}-${afterLine.newLine}-${index}`}
          data-key="code-line"
          onMouseEnter={() =>
            onSelectionMove?.(chunkIndex || 0, index, { before, after })
          }
          onMouseUp={onSelectionEnd}
          role="row"
          tabIndex={-1}
        >
          <CodeLine
            activePermalink={activePermalink}
            filePath={filePath}
            index={index}
            chunkIndex={chunkIndex}
            line={beforeLine}
            highlightedContent={addWordDiffToHighlighting(
              getHighlightedLine(
                beforeLine,
                before,
                after,
                beforeHighlighted,
                afterHighlighted
              ),
              beforeLine
            )}
            showPermalinks={showPermalinks}
            hideNewLines
            onPermalinkClick={onPermalinkClick}
            onAddComment={onAddComment}
            inlineContent={
              lineHasInlineContent(beforeLine, inlineContent)
                ? inlineContent
                : undefined
            }
            highlightLine={shouldHighlightDiffLine?.(beforeLine)}
            highlightDivider={shouldHighlightDivider}
            onSelectionStart={onSelectionStart}
            isMultilineSelecting={isMultilineSelecting}
            isMultilineSelectionStartLine={
              chunkIndex === selectionChunkIndex &&
              index === selectionStartLineIndex
            }
          />
          <CodeLine
            activePermalink={activePermalink}
            filePath={filePath}
            index={index}
            chunkIndex={chunkIndex}
            line={afterLine}
            highlightedContent={addWordDiffToHighlighting(
              getHighlightedLine(
                afterLine,
                before,
                after,
                beforeHighlighted,
                afterHighlighted
              ),
              afterLine
            )}
            showPermalinks={showPermalinks}
            hideOldLines
            onPermalinkClick={onPermalinkClick}
            onAddComment={onAddComment}
            lineAnnotations={getCurrentLineAnnotations(
              fileAnnotations,
              afterLine.newLine
            )}
            isPrAnnotationsEnabled={isPrAnnotationsEnabled}
            inlineContent={
              !isUnchanged && lineHasInlineContent(afterLine, inlineContent)
                ? inlineContent
                : undefined
            }
            highlightLine={shouldHighlightDiffLine?.(afterLine)}
            highlightDivider={shouldHighlightDivider}
            onSelectionStart={onSelectionStart}
            isMultilineSelecting={isMultilineSelecting}
            isMultilineSelectionStartLine={
              chunkIndex === selectionChunkIndex &&
              index === selectionStartLineIndex
            }
          />
        </div>
      );
    });

    return (
      <ChunkControls
        before={chunk.extra.before}
        heading={chunk.content}
        isLoadingBelow={chunk.extra.isLoadingBelow}
        isLoadingAbove={chunk.extra.isLoadingAbove}
        onShowMoreLines={onShowMoreLines}
        isSideBySide
        isDummyChunk={chunk.isDummyChunk}
        isFirstChunk={isFirstChunk}
        isLastChunk={isLastChunk}
        isMultiChunkExpansionInDiffEnabled={isMultiChunkExpansionInDiffEnabled}
      >
        {diffLines}
      </ChunkControls>
    );
  }
);

export const UnifiedDiffLines = memo(
  ({
    chunk,
    chunkIndex,
    onShowMoreLines,
    isFirstChunk,
    isLastChunk,
    before,
    after,
    activePermalink,
    filePath,
    showPermalinks,
    onPermalinkClick,
    onAddComment,
    fileAnnotations,
    inlineContent,
    isPrAnnotationsEnabled,
    beforeHighlighted,
    afterHighlighted,
    isMultiChunkExpansionInDiffEnabled,
    shouldHighlightDiffLine,
    shouldHighlightLineDivider,
    onSelectionStart,
    onSelectionMove,
    onSelectionEnd,
    isMultilineSelecting,
    selectionChunkIndex,
    selectionStartLineIndex,
  }: DiffLinesProps) => {
    const diffLines = chunk.changes.map((line, index) => (
      <div
        className="lines-wrapper"
        key={`line-${line.oldLine}-${line.newLine}-${index}`}
        data-key="code-line"
        onMouseEnter={() => onSelectionMove?.(chunkIndex || 0, index, chunk)}
        onMouseUp={onSelectionEnd}
        role="row"
        tabIndex={-1}
      >
        <CodeLine
          activePermalink={activePermalink}
          filePath={filePath}
          index={index}
          chunkIndex={chunkIndex}
          line={line}
          highlightedContent={addWordDiffToHighlighting(
            getHighlightedLine(
              line,
              before,
              after,
              beforeHighlighted,
              afterHighlighted
            ),
            line
          )}
          showPermalinks={showPermalinks}
          onPermalinkClick={onPermalinkClick}
          onAddComment={onAddComment}
          lineAnnotations={getCurrentLineAnnotations(
            fileAnnotations,
            line.newLine
          )}
          isPrAnnotationsEnabled={isPrAnnotationsEnabled}
          inlineContent={
            lineHasInlineContent(line, inlineContent)
              ? inlineContent
              : undefined
          }
          highlightLine={shouldHighlightDiffLine?.(line)}
          highlightDivider={
            shouldHighlightLineDivider?.(chunkIndex || 0, index) ?? false
          }
          onSelectionStart={onSelectionStart}
          isMultilineSelecting={isMultilineSelecting}
          isMultilineSelectionStartLine={
            chunkIndex === selectionChunkIndex &&
            index === selectionStartLineIndex
          }
        />
      </div>
    ));

    return (
      <ChunkControls
        before={chunk.extra.before}
        heading={chunk.content}
        isLoadingBelow={chunk.extra.isLoadingBelow}
        isLoadingAbove={chunk.extra.isLoadingAbove}
        onShowMoreLines={onShowMoreLines}
        isSideBySide={false}
        isDummyChunk={chunk.isDummyChunk}
        isFirstChunk={isFirstChunk}
        isLastChunk={isLastChunk}
        isMultiChunkExpansionInDiffEnabled={isMultiChunkExpansionInDiffEnabled}
      >
        {diffLines}
      </ChunkControls>
    );
  }
);

function addWordDiffToHighlighting(
  segment: CodeSegment | undefined,
  line: Line
): CodeSegment | undefined {
  if (segment && line.wordDiffRegions) {
    if (line.newLine) {
      const newPairs = line.wordDiffRegions.revisedRegions.map(
        (region: Region): [number, number] => [region.start, region.end]
      );
      return emphasizeMatches(segment, newPairs, 'ins');
    }
    const origPairs = line.wordDiffRegions.originalRegions.map(
      (region: Region): [number, number] => [region.start, region.end]
    );
    return emphasizeMatches(segment, origPairs, 'del');
  }
  return segment;
}

function getHighlightedLine(
  currentLine: Line,
  before: ChunkEntry,
  after: ChunkEntry,
  beforeHighlighted: CodeSegment[],
  afterHighlighted: CodeSegment[]
): CodeSegment | undefined {
  // To find the correct highlighted line, we index into our highlighted
  // line arrays. The index in the arrays (which contain only the lines in
  // this chunk) is the difference between the current line's line number
  // and the line number of the first line in the chunk.
  //
  // We can't use the iteration index here because some lines exist on
  // only the original source or only on the new source, causing the
  // iteration index to not line up with the highlighted arrays.
  if (currentLine.newLine) {
    return afterHighlighted[currentLine.newLine - after.newStart];
  }
  if (currentLine.oldLine) {
    return beforeHighlighted[currentLine.oldLine - before.oldStart];
  }
  return undefined;
}
